import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

/**
 * App-bar title context. Each page declares its title via {@link usePageTitle};
 * the topbar renders it (with a brand-wordmark fallback on pages that set none,
 * e.g. auth/legal pages). This lets the mobile app-bar carry page context — "Каталог
 * тендеров", a region/category name, a tender label — so the body H1 can be
 * sr-only and the title row reclaimed, without the topbar re-fetching anything.
 */
const PageTitleContext = createContext<{
  title: string | null;
  setTitle: (t: string | null) => void;
} | null>(null);

export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<string | null>(null);
  return (
    <PageTitleContext.Provider value={{ title, setTitle }}>{children}</PageTitleContext.Provider>
  );
}

/** The current app-bar title (null → the topbar shows the brand wordmark). */
export function usePageTitleValue(): string | null {
  return useContext(PageTitleContext)?.title ?? null;
}

/** Declare the app-bar title for the lifetime of the calling page. Resets to the
 *  brand wordmark when the page unmounts. `null` opts a page out (keeps wordmark). */
export function usePageTitle(title: string | null): void {
  const ctx = useContext(PageTitleContext);
  const setTitle = ctx?.setTitle;
  useEffect(() => {
    setTitle?.(title);
    return () => setTitle?.(null);
  }, [setTitle, title]);
}
